import * as Icons from "lucide-react";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { Reveal } from "../components/motion";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { SectionHeading, SectionShell, type SectionProps } from "./SectionRenderer";

function iconFor(name: string, fallback: keyof typeof Icons = "Quote") {
  const key = (name || fallback) as keyof typeof Icons;
  return (Icons[key] as typeof Icons.Quote) ?? (Icons[fallback] as typeof Icons.Quote);
}

/**
 * A titled band carrying the words the organisation stands on.
 *
 * The heading sits outside on the page ground, the way every other section on
 * the site is titled; everything the band is about — the values, and the motto
 * line when there is one — sits inside the fill. That split is the point. A
 * heading inside a filled band competes with the band's own contents for the
 * eye, and the reader loses the thread of the page's outline.
 *
 * Deep blue rather than navy: the band sits on the light ground that carries
 * the vision and mission, and navy-900 there reads as the start of a new
 * full-width section rather than as an object resting on the page.
 *
 * `plain` drops the fill for pages where a second filled band in a row would be
 * one too many — the rule and the gold marks still set the row apart.
 */
export default function MottoBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["motto"]>) {
  const motto = content.motto?.trim();
  const items = (content.items ?? []).filter((item) => item.label?.trim());

  // Either half stands on its own: a motto line with no values, or the values
  // while an editor is still deciding on the line. Nothing at all renders
  // nothing, rather than an empty band the width of the page.
  if (!motto && items.length === 0) return null;

  const Icon = iconFor(content.icon);
  const banded = content.layout !== "plain";
  const centered = settings.alignment === "center";

  /*
    The emblem well is a white tile whether or not it holds an image: a QR code
    or a crest needs a light ground to be readable at all, and the icon reading
    gold-on-white keeps the two variants the same shape and weight, so swapping
    one for the other does not change the band's balance.
  */
  const mark = (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-xl",
        content.emblem ? "size-20 bg-white p-1.5 sm:size-24" : "size-14 sm:size-16",
        content.emblem ? "" : banded ? "bg-white/10" : "bg-gold-500/10",
      )}
    >
      {content.emblem ? (
        <ResponsiveImage
          image={content.emblem}
          sizes="96px"
          className="size-full object-contain"
        />
      ) : (
        <Icon
          aria-hidden
          strokeWidth={1.5}
          className={cn("size-7 sm:size-8", banded ? "text-gold-300" : "text-gold-600")}
        />
      )}
    </span>
  );

  return (
    <SectionShell settings={settings}>
      {/*
        Sized to the prose headings rather than to a section heading: this block
        is slotted between a vision and a mission set as rich-text h2s, and at
        the standard section size its title reads as ranking above them instead
        of alongside them.
      */}
      <SectionHeading
        eyebrow={content.eyebrow}
        heading={content.heading}
        settings={settings}
        size="prose"
      />

      <Reveal
        className={cn(
          banded
            ? "rounded-3xl bg-navy-700 px-6 py-7 sm:px-10 sm:py-9"
            : "border-t border-gold-500/40 pt-7",
        )}
      >
        {motto && (
          <div className={cn("flex items-center gap-5 sm:gap-8", centered && "justify-center")}>
            {content.icon || content.emblem ? mark : null}
            <div className="min-w-0">
              <p
                className={cn(
                  // Uppercase and tightly tracked, the way the motto is set on
                  // the printed material this mirrors. The schema caps the
                  // length, so it cannot grow into a paragraph that this
                  // treatment would make unreadable.
                  "text-xl font-semibold uppercase leading-tight tracking-wide sm:text-3xl",
                  banded ? "text-cream-50" : "text-navy-900",
                )}
              >
                {motto}
              </p>
              {content.supporting && (
                <p
                  className={cn(
                    "mt-1.5 text-sm uppercase tracking-[0.2em] sm:text-base",
                    banded ? "text-navy-200" : "text-ink-600",
                  )}
                >
                  {content.supporting}
                </p>
              )}
            </div>
          </div>
        )}

        {items.length > 0 && (
          /*
            Spread edge to edge once they all fit on one line, which is the
            arrangement these are designed for. Below that they wrap and pack
            left, where `justify-between` would strand the last row's items at
            opposite ends of the band.
          */
          <ul
            className={cn(
              "flex flex-wrap items-center gap-x-8 gap-y-5 sm:gap-x-12 lg:justify-between",
              // A hairline only when there is a motto line above to divide it
              // from — on its own the band's edge is the only boundary the row
              // needs.
              motto && (banded ? "mt-7 border-t border-white/15 pt-7" : "mt-7"),
            )}
          >
            {items.map((item, index) => {
              const ItemIcon = iconFor(item.icon, "Circle");
              return (
                <li key={index} className="flex items-center gap-2.5">
                  <ItemIcon
                    aria-hidden
                    strokeWidth={1.75}
                    className={cn(
                      "size-5 shrink-0 sm:size-6",
                      banded ? "text-gold-300" : "text-gold-600",
                    )}
                  />
                  <span
                    className={cn(
                      "text-sm font-semibold uppercase tracking-[0.12em] sm:text-base",
                      banded ? "text-cream-50" : "text-navy-900",
                    )}
                  >
                    {item.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Reveal>
    </SectionShell>
  );
}
